import { useFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { useNote } from "@knowt/syncing/hooks/notes/useNotes";
import { X } from "lucide-react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import CircularButton from "../CircularButton";
import { themeColors } from "@/utils/themeColors";
import { iconSizes } from "@/utils/iconProps";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import { useCardPreviewContextSelector } from "@/contexts/CardPreviewContext";
import SlideMenu from "@/components/SlideMenu";
import Dialog from "@/components/Dialog";
import LazyLoaded from "@/components/LazyLoaded";
import { useStateWithHistory } from "react-use";

const ContentPreviewer = dynamic(() => import("./ContentPreviewer"));

const ContentPreviewerContainer = dynamic(() =>
    import("./ContentPreviewer").then(mod => mod.ContentPreviewerContainer)
);

const ContentPreviewerPopup = () => {
    const { smDown } = useBreakPoints();

    const noteId = useCardPreviewContextSelector(state => state.noteId);
    const flashcardSetId = useCardPreviewContextSelector(state => state.flashcardSetId);
    const closeCardPreview = useCardPreviewContextSelector(state => state.closeCardPreview);
    const isOpen = useCardPreviewContextSelector(state => state.isOpen);

    const { note } = useNote({ noteId });
    const { flashcardSet } = useFlashcardSet({ flashcardSetId });

    const pathname = usePathname();

    // disable this popup on search page (note tab and flashcardSet tab)
    const isSearchPage = pathname?.includes("/search");

    const [_, setPaths, paths] = useStateWithHistory(pathname);
    useEffect(() => {
        setPaths(pathname);
    }, [pathname, setPaths]);
    // hacky way to fix this: https://streamable.com/p5mmf6
    // where the previewer popup opened when user goes away from `isSubjectPage` or `isSearchPage`
    useEffect(() => {
        const prevPath = paths.history.at(-1);
        if (prevPath === "/search" && !isSearchPage) {
            closeCardPreview();
        }
    }, [closeCardPreview, isSearchPage, paths]);

    const isNote = !flashcardSet && noteId;
    const isFlashcardSet = !note && flashcardSetId;

    const renderDesktopContent = () => (
        <Dialog
            slotProps={{
                paper: {
                    style: {
                        width: "87.7rem",
                        maxWidth: "200rem",
                        borderRadius: "1.5rem",
                        boxShadow: "none",
                        height: "90.4rem",
                        backgroundColor: themeColors.background,
                        color: "unset",
                    },
                },
            }}
            open={isOpen && !isSearchPage && (!!isNote || !!isFlashcardSet)}
            onClose={closeCardPreview}>
            <ContentPreviewerContainer style={{ padding: "3.3rem 4.1rem" }}>
                <LazyLoaded load={isOpen}>
                    <ContentPreviewer parentPadding={{ right: "4.1rem", bottom: "3.3rem" }} />
                </LazyLoaded>
            </ContentPreviewerContainer>
        </Dialog>
    );

    const renderMobileContent = () => (
        <SlideMenu isOpen={isOpen && !isSearchPage && (!!isNote || !!isFlashcardSet)} onClose={closeCardPreview}>
            <ContentPreviewerContainer style={{ padding: "2rem" }}>
                <CircularButton
                    onClick={closeCardPreview}
                    radius="5rem"
                    sx={{
                        border: `1px solid ${themeColors.neutralBlack}`,
                        margin: "0 0 2rem auto",
                    }}>
                    <X size={iconSizes.MD + 5} />
                </CircularButton>
                <LazyLoaded load={isOpen}>
                    <ContentPreviewer parentPadding={{ right: "2rem", bottom: "2rem" }} />
                </LazyLoaded>
            </ContentPreviewerContainer>
        </SlideMenu>
    );

    return smDown ? renderMobileContent() : renderDesktopContent();
};

export default ContentPreviewerPopup;
